// Route-level RBAC. Видимость и доступ — per Role.
// STAITER_UX §«Roles»: accountant / reviewer / sme / warehouse_operator / production_planner / admin / manager / viewer
import * as m from '@staiter/i18n';
import type { Role } from '@staiter/shared';
import {
  ROLE_DEFAULT_MODULES,
  isModuleExempt,
  moduleForRoute,
} from '@staiter/shared/engines-pure/module-access';

export type RouteKey =
  | 'dashboard'
  | 'runs'
  | 'conflicts'
  | 'anomalies'
  | 'evidence'
  | 'items'
  | 'warehouses'
  | 'batches'
  | 'boms'
  | 'stock'
  | 'stock-entries'
  | 'stock-reconciliation'
  | 'work-orders'
  | 'landed-cost'
  | 'chat'
  | 'excel'
  | 'admin'
  | 'approvals'
  | 'reconciliation'
  | 'writeoff'
  | 'cost'
  | 'period-close'
  // Phase 2 finance (S13-S16 + 2026-04-24 extensions).
  | 'finance-cogs'
  | 'finance-cash-flow'
  | 'finance-pnl'
  | 'finance-regulatory'
  | 'finance-dashboard'
  | 'counterparties'
  | 'invoices'
  | 'payments'
  | 'settlements'
  | 'budgets'
  | 'docs';

// undefined = доступно всем авторизованным. Явный список = allow-list.
const ALLOW: Partial<Record<RouteKey, Role[]>> = {
  // С-I8 Р.5 (В-59): руководитель компании входит в админку — реестр своей компании (`/admin/users`).
  admin: ['admin', 'company_admin'],
  // inventory-ops — только ops-роли + admin/manager
  'stock-entries': ['warehouse_operator', 'production_planner', 'admin', 'manager', 'accountant'],
  // С-G Р.4: роли зон заявки уровня 3 видят документ, который подписывают (SOLUTION § 9.1) —
  // руководитель компании второй подписывает отмену завершённого заказа, ЭСФ/КПН, пакеты
  // документов (счета, платежи, взаиморасчёты).
  'work-orders': ['production_planner', 'admin', 'manager', 'accountant', 'company_admin'],
  'landed-cost': ['accountant', 'admin', 'manager'],
  'stock-reconciliation': ['warehouse_operator', 'accountant', 'admin', 'manager'],
  // S6 workspaces — только ответственные роли
  // С-G Р.4: «Согласования» — всем, у кого модуль `agent_review` (экран — ModuleGuard, сервер —
  // `/approvals/*` под тем же модулем): адресат заявки уровня 3 — руководитель компании,
  // проектов или производства, и раньше пункт у них был неактивным.
  reconciliation: ['accountant', 'reviewer', 'sme', 'admin'],
  writeoff: ['accountant', 'reviewer', 'sme', 'admin'],
  cost: ['accountant', 'reviewer', 'sme', 'admin'],
  'period-close': ['accountant', 'reviewer', 'sme', 'admin'],
  // Phase 2 finance — accountant + manager + admin (regulatory + AR/AP).
  'finance-cogs': ['accountant', 'manager', 'admin'],
  'finance-cash-flow': ['accountant', 'manager', 'admin'],
  'finance-pnl': ['accountant', 'manager', 'admin'],
  'finance-regulatory': ['accountant', 'manager', 'admin', 'company_admin'],
  'finance-dashboard': ['accountant', 'manager', 'admin', 'reviewer', 'sme', 'viewer'],
  counterparties: ['accountant', 'manager', 'admin', 'company_admin'],
  invoices: ['accountant', 'manager', 'admin', 'company_admin'],
  payments: ['accountant', 'manager', 'admin', 'company_admin'],
  settlements: ['accountant', 'manager', 'admin', 'company_admin'],
  budgets: ['accountant', 'manager', 'admin'],
};

export function canAccess(role: Role | undefined, key: RouteKey): boolean {
  if (!role) return false;
  const allow = ALLOW[key];
  if (!allow) return true;
  return allow.includes(role);
}

/**
 * access-control (C4) — cross-role ALLOW.
 * Если модуль роута явно выдан гранатом СВЕРХ дефолта роли (`module_access` содержит
 * модуль, которого нет в ROLE_DEFAULT_MODULES[role]) — роут доступен, даже если
 * role-based `canAccess` его не пускает. Иначе — обычный canAccess (внутри дефолтных
 * модулей fine-gating по роли сохраняется). `moduleAccess===undefined` → старый BE → canAccess.
 */
export function effectiveCanAccess(
  role: Role | undefined,
  key: RouteKey,
  routePath: string,
  moduleAccess: string[] | undefined,
): boolean {
  if (!role) return false;
  // Маршруты-исключения (общие мастер-данные, напр. /counterparties) — доступны всем
  // авторизованным независимо от роли и module_access (решение 2026-06-30).
  if (isModuleExempt(routePath)) return true;
  if (moduleAccess !== undefined) {
    const mod = moduleForRoute(routePath);
    if (!moduleAccess.includes(mod)) return false;
    const defaults = ROLE_DEFAULT_MODULES[role] ?? [];
    if (!defaults.includes(mod)) return true; // explicit grant beyond role → open routes
  }
  return canAccess(role, key);
}

/**
 * S8 «unreachable visible» pattern: возвращает required-роли для маршрута
 * (для рендера tooltip «Требуется роль: …»). undefined = доступно всем.
 */
export function requiredRolesFor(key: RouteKey): Role[] | undefined {
  return ALLOW[key];
}

export function formatRequiredRoles(roles: Role[]): string {
  return roles.map(getRoleLabel).join(' / ');
}

/**
 * Курируемый набор ролей при добавлении пользователя в компанию (entity membership) —
 * продуктовое решение 2026-06-30. НЕ весь MembershipRole enum: только эти показываем
 * во всех формах приглашения/смены роли (единый источник, без разнобоя).
 *  - company_admin «Руководитель компании» — доступ везде внутри своей компании
 *  - admin «Админ» — доступ везде (по всем компаниям)
 *  - manager «Руководитель проектов»
 *  - accountant «Бухгалтер»
 *  - production_planner «Руководитель производства»
 *  - reviewer «Контролёр» — N1/N3 (заход С6): роль ЖИВЁТ в membership-строках прода,
 *    но в наборе её не было, и вернуть её через форму «Доступы» было нельзя. При
 *    прод-проверке P1 восстановление членства пришлось делать прямым UPDATE в БД —
 *    штатного пути не существовало. Набор обязан покрывать то, что реально есть.
 *  - sme «Эксперт» — то же основание: в проде живёт legacy-значение `expert`, которое
 *    `getRoleLabel` маппит в canonical `sme` (см. `legacyAlias` ниже).
 *  - viewer «Наблюдатель»
 *
 * Инвариант: каждая роль набора ОБЯЗАНА быть в `ROLE_RANK`/`ABILITIES` бэкенда
 * (`middleware/rbac.ts`) — иначе `rankOf=0` и роль проваливает все `requireRole`
 * (инцидент 2026-06-22 с `company_admin`). Проверено: `reviewer`=50, `sme`=40.
 * Названия берутся из `getRoleLabel`, здесь ничего не хардкодится.
 */
/**
 * F1 (service-role) — `service` в набор НЕ добавлена, и это осознанное исключение,
 * а не пропуск.
 *
 * Сервисные аккаунты заводятся отдельным решением человека, а не выбором из
 * выпадающего списка рядом с «Бухгалтером»: дать машине доступ к компании — это не
 * то же самое, что добавить сотрудника. Причина записана здесь, потому что сторож
 * `B8` требует от каждой роли либо присутствия в наборе, либо явного исключения —
 * иначе следующий добавит её «для полноты».
 */
export const ENTITY_MEMBERSHIP_ROLES: Role[] = [
  'company_admin',
  'admin',
  'manager',
  'accountant',
  'production_planner',
  'reviewer',
  'sme',
  'viewer',
];

/**
 * Каноническая посадка после входа — «Задачи» (go-live 2026-07-03: модуль задач —
 * основная рабочая поверхность для всех; никаких промежуточных прыжков через дашборд).
 * Дашборд (`/`) остаётся доступен из меню тем, у кого есть agent_review.
 */
export const DEFAULT_LANDING_ROUTE = '/work-tasks';

/**
 * Стартовый маршрут после входа/смены компании: задачи → проекты → дашборд → …
 * Если модуль первого кандидата запрещён — уводим на следующий доступный раздел.
 * `/counterparties` (exempt) — гарантированный fallback. Решение 2026-06-30.
 */
const LANDING_CANDIDATES: Array<{ path: string; module: string }> = [
  { path: '/work-tasks', module: 'tasks' },
  { path: '/projects', module: 'projects' },
  { path: '/', module: 'agent_review' },
  { path: '/items', module: 'inventory' },
  { path: '/work-orders', module: 'production' },
  { path: '/finance/dashboard', module: 'finance' },
  { path: '/excel', module: 'reporting' },
  { path: '/admin', module: 'admin' },
];

export function resolveLandingRoute(moduleAccess: string[] | undefined): string {
  if (moduleAccess === undefined) return DEFAULT_LANDING_ROUTE;
  const hit = LANDING_CANDIDATES.find((c) => moduleAccess.includes(c.module));
  return hit?.path ?? '/counterparties';
}

/** Путь — «посадочный» кандидат? Для авто-редиректа ModuleGuard вместо тупика «Нет доступа». */
export function isLandingCandidate(pathname: string): boolean {
  return LANDING_CANDIDATES.some((c) => c.path === pathname);
}

/**
 * Названия ролей. SSOT остался здесь, но сами строки переехали в `@staiter/i18n`.
 *
 * CC-6: функция возвращала русские литералы константами, мимо i18n. Прод-проверка
 * en-локали живым кликом 26.07 показала «Контролёр», «Наблюдатель», «Руководитель
 * проектов» на КАЖДОМ экране, где роль вообще упоминается — форма «Доступы», карточка
 * пользователя, чипы ролей. Барьер `translation-drift` этого не видел и не мог:
 * этих строк в `ru.json` не было вовсе.
 *
 * Ключи объявлены в обеих локалях (`role_label_*`) и проходят барьер «en == ru и
 * содержит кириллицу → не переведено».
 */
export function getRoleLabel(role: Role | string): string {
  const map: Record<Role, () => string> = {
    accountant: m.role_label_accountant,
    reviewer: m.role_label_reviewer,
    sme: m.role_label_sme,
    warehouse_operator: m.role_label_warehouse_operator,
    production_planner: m.role_label_production_planner,
    admin: m.role_label_admin,
    manager: m.role_label_manager,
    viewer: m.role_label_viewer,
    company_admin: m.role_label_company_admin,
    /**
     * F1 (service-role) — подпись сервисной роли.
     *
     * Роль без подписи показывается сырым идентификатором `service` на каждом
     * экране, где роль вообще упоминается. В `ENTITY_MEMBERSHIP_ROLES` она при этом
     * НЕ добавляется — см. комментарий там: набор курируемый.
     */
    service: m.role_label_service,
  };
  // FE-222 Pass 95: defensive fallback — BE legacy roles ('expert' и т.д.) map к canonical
  // через z.preprocess в LegacyRole, но на случай неизвестных значений возвращаем raw.
  const legacyAlias: Record<string, Role> = {
    expert: 'sme',
  };
  // FM-S-multi-6: holding-роли + legacy 'member' вне canonical Role enum.
  const extra: Record<string, () => string> = {
    holding_admin: m.role_label_holding_admin,
    holding_viewer: m.role_label_holding_viewer,
    member: m.role_label_member,
  };
  const extraFn = extra[role as string];
  if (extraFn) return extraFn();
  const canonical = (legacyAlias[role as string] ?? (role as Role)) as Role;
  return map[canonical]?.() ?? String(role);
}

/**
 * F21 (пак tails-closeout, после B33 / В-2): экран «Доступы» сотрудника открыт
 * `company_admin` — сервер управляет членствами, складами и модулями в пределах его
 * компании (`assertActorCanManageEntity`). С-I8 Р.5 (В-59, ACC-F18): и реестр пользователей
 * (`/admin/users`) — сервер отдаёт ему людей своей компании и приглашение; вход `/admin` ведёт в
 * реестр. Остальная админка — только `admin`: интеграции, матрица согласований, компании.
 */
const COMPANY_ADMIN_ADMIN_PATHS = [
  /^\/admin\/?$/,
  /^\/admin\/users\/?$/,
  /^\/admin\/users\/[^/]+\/memberships\/?$/,
];

export function canOpenAdminPath(role: string | undefined, pathname: string): boolean {
  if (role === 'admin') return true;
  if (role === 'company_admin') return COMPANY_ADMIN_ADMIN_PATHS.some((re) => re.test(pathname));
  return false;
}
